import { useEffect, useRef, useState } from "react";
import { useFormContext } from "components/CandidateCreation/context/Context";
import { ApiCall } from "components/common/services/ApiServices";
import { Accordion } from "react-bootstrap";
import CheckBoxField from "components/common/atoms/CheckBoxField";
import RadioField from "components/common/atoms/RadioField";
import LabelWithTextAreaField from "components/common/molecules/LabelWithTextAreaField";
import Button from "components/common/atoms/Button";
import CustomNotify from "components/common/atoms/CustomNotify";
import * as ENDPOINTS from "routes/ApiEndpoints";
import {
  t,
  translate,
} from "components/CentralDataMangement/translation/Translation";

/**
 * Read-with-answers accordion shown under the Function field on the Desired job tab.
 *
 * Renders the same function-related question/answer accordion as the screening page
 * (without the feedback/info block). Questions are fetched reactively for the selected
 * functions; answers live in `state.jobProfile.functionQuestions` so they persist with
 * the candidate form.
 */
const DesiredJobQuestions: React.FC = () => {
  const { state, dispatch, isViewMode } = useFormContext();

  const desiredFunctions: any[] = state.jobProfile.desiredFunctions ?? [];
  const questions: any[] = state.jobProfile.functionQuestions ?? [];

  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  // Latest saved answers, readable inside the fetch effect without retriggering it.
  const savedRef = useRef<any[]>(questions);
  savedRef.current = questions;

  const functionKey = desiredFunctions.map((f: any) => f.value).join(",");

  const setQuestions = (value: any[]) => {
    dispatch({
      type: "UPDATE_JOB_PROFILE_FIELD",
      field: "functionQuestions",
      value,
    });
  };

  useEffect(() => {
    if (desiredFunctions.length === 0) {
      if (savedRef.current.length > 0) {
        setQuestions([]);
      }
      return;
    }
    fetchQuestionsByFunction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [functionKey]);

  // Overlay already-saved answers onto a freshly fetched question set.
  const mergeAnswers = (fresh: any[], saved: any[]) =>
    fresh.map((group: any) => {
      const savedGroup = saved.find(
        (g: any) => g?.function?.value === group?.function?.value
      );
      return {
        ...group,
        questions: (group.questions ?? []).map((question: any) => {
          const savedQuestion = savedGroup?.questions?.find(
            (q: any) => q.questionId === question.questionId
          );
          return savedQuestion
            ? {
                ...question,
                answers: savedQuestion.answers ?? [],
                disabled: savedQuestion.disabled ?? false,
              }
            : question;
        }),
      };
    });

  const fetchQuestionsByFunction = async () => {
    const postData = { functions: desiredFunctions, questions: [] };
    const response = await ApiCall.service(
      `${ENDPOINTS.GET_QUESTIONS_BY_FUNCTIONS}`,
      "POST",
      postData,
      false,
      "central-data-management"
    );
    if (response?.status === 200) {
      setQuestions(mergeAnswers(response?.data ?? [], savedRef.current));
    } else if (response?.status === 400) {
      CustomNotify({ type: "error", message: t(response?.data) });
      if (savedRef.current.length > 0) {
        setQuestions([]);
      }
    }
  };

  const handleFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    groupIndex: number,
    questionIndex: number
  ) => {
    if (isViewMode) return;
    const { name, type, value } = event.target;

    const updated = questions.map((group: any, gIdx: number) => {
      if (gIdx !== groupIndex) return group;
      return {
        ...group,
        questions: group.questions.map((question: any, qIdx: number) => {
          if (qIdx !== questionIndex) return question;
          let answers = question.answers ? [...question.answers] : [];
          if (type === "radio") {
            answers = [parseInt(value)];
          } else if (type === "checkbox") {
            const optionValue = parseInt(value);
            if ((event.target as HTMLInputElement).checked) {
              if (!answers.includes(optionValue)) answers.push(optionValue);
            } else {
              answers = answers.filter((a: number) => a !== optionValue);
            }
          } else if (name === "description") {
            answers = [value];
          }
          return { ...question, answers };
        }),
      };
    });
    setQuestions(updated);
  };

  const handleDisableQuestion = (groupIndex: number, questionIndex: number) => {
    if (isViewMode) return;
    const updated = questions.map((group: any, gIdx: number) => {
      if (gIdx !== groupIndex) return group;
      return {
        ...group,
        questions: group.questions.map((question: any, qIdx: number) => {
          if (qIdx !== questionIndex) return question;
          return { ...question, disabled: !question.disabled, answers: [] };
        }),
      };
    });
    setQuestions(updated);
  };

  const toggleAccordion = (groupIndex: number) => {
    setActiveKeys((prevKeys) =>
      prevKeys.includes(`${groupIndex}`)
        ? prevKeys.filter((key) => key !== `${groupIndex}`)
        : [...prevKeys, `${groupIndex}`]
    );
  };

  if (questions.length === 0) return null;

  return (
    <div className="form-border marginBotttom1 mt-3">
      {questions.map((questionGroup: any, groupIndex: number) => (
        <div key={groupIndex}>
          <div className="margnBotttom1">
            <Accordion activeKey={activeKeys}>
              <Accordion.Item eventKey={`${groupIndex}`}>
                <Accordion.Header
                  onClick={() => toggleAccordion(groupIndex)}
                  className="fw-bold"
                >
                  {t(`${questionGroup.function.label}`)}
                </Accordion.Header>
                <Accordion.Body className="bg-white padding1">
                  {questionGroup.questions.length > 0 ? (
                    <>
                      {questionGroup.questions.map(
                        (question: any, index: number) => (
                          <div
                            key={index}
                            className={`marginBotttom1 internalContentWrapper border ${
                              question.disabled
                                ? " border question-not-applicable-section opacity-50 marginBottom1 interbalContebtWrapper"
                                : ""
                            }`}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <h5
                                className="questionName"
                                dangerouslySetInnerHTML={{
                                  __html: index + 1 + ") " + question.question,
                                }}
                              />
                              {!isViewMode && (
                                <Button
                                  className="btn py-1 px-3 border-0 rounded-2 text-end not-applicable-action"
                                  handleClick={() =>
                                    handleDisableQuestion(groupIndex, index)
                                  }
                                >
                                  {t("N/A")}
                                </Button>
                              )}
                            </div>

                            {question.questionType === 1 &&
                              question.options?.length > 0 && (
                                <div className="my-1">
                                  {question.options.map(
                                    (option: any, optionIndex: number) => (
                                      <div
                                        key={optionIndex}
                                        className="screening-checkbox d-flex align-items-center mb-2"
                                      >
                                        <CheckBoxField
                                          name={`answers-${groupIndex}-${index}-${optionIndex}`}
                                          id={`answers-${groupIndex}-${index}-${optionIndex}`}
                                          onChangeHandler={(event) =>
                                            handleFieldChange(
                                              event,
                                              groupIndex,
                                              index
                                            )
                                          }
                                          value={optionIndex}
                                          disable={
                                            question.disabled || isViewMode
                                          }
                                          isChecked={question.answers.includes(
                                            optionIndex
                                          )}
                                        />
                                        <span
                                          dangerouslySetInnerHTML={{
                                            __html: option.replace(
                                              /<a/g,
                                              '<a target="_blank"'
                                            ),
                                          }}
                                        />
                                      </div>
                                    )
                                  )}
                                </div>
                              )}

                            {question.questionType === 3 &&
                              question.options?.length > 0 && (
                                <div className="my-1">
                                  {question.options.map(
                                    (option: any, optionIndex: number) => (
                                      <div
                                        key={optionIndex}
                                        className="d-flex screening-checkbox align-items-center mb-2"
                                      >
                                        <RadioField
                                          className="ps-1"
                                          id={`answers-${groupIndex}-${index}-${optionIndex}`}
                                          value={optionIndex}
                                          name={`answers-${groupIndex}-${index}`}
                                          handleChange={(event) =>
                                            handleFieldChange(
                                              event,
                                              groupIndex,
                                              index
                                            )
                                          }
                                          disable={
                                            question.disabled || isViewMode
                                          }
                                          ischecked={question.answers.includes(
                                            optionIndex
                                          )}
                                        />
                                        <span
                                          dangerouslySetInnerHTML={{
                                            __html: option,
                                          }}
                                        />
                                      </div>
                                    )
                                  )}
                                </div>
                              )}

                            {question.questionType === 2 && (
                              <LabelWithTextAreaField
                                name="description"
                                handleChange={(event) =>
                                  handleFieldChange(event, groupIndex, index)
                                }
                                label=""
                                value={question.answers[0] ?? ""}
                                isDisabled={question.disabled || isViewMode}
                              />
                            )}
                          </div>
                        )
                      )}
                    </>
                  ) : (
                    <div className="text-danger p-2 text-center">
                      {t("No general and function related questions added")}
                    </div>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </div>
        </div>
      ))}
    </div>
  );
};

export default translate(DesiredJobQuestions);
